import type {
  NavigationGuardNext,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { useStore } from '@/stores'

const LOBBY_URL = import.meta.env.VITE_LOBBY_URL

/**
 * Initialize middleware - checks if store has been initialized and initializes it if needed
 * Does not check if logged in, assumes authentication and redirects on 403
 *
 * Design Decision: This middleware is async and waits for store initialization to complete
 * before allowing the route to proceed. This ensures that components can assume the store
 * data is available immediately, simplifying component logic at the cost of slightly slower
 * initial page loads. Alternative would be fire-and-forget approach with loading states.
 */
export const initialize = async (
  _to: RouteLocationNormalizedGeneric,
  _from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
): Promise<void> => {
  try {
    const store = useStore()

    // Check if store is already loaded/initialized
    if (!store.isLoaded) {
      try {
        await store.storeInitialize()
      } catch (error: any) {
        console.error('Store initialization failed:', error)
        // Handle 403 errors by redirecting based on environment
        if (error?.response?.status === 403) {
          window.location.href = LOBBY_URL
          return
        }
        // For other errors, still proceed but log the error
      }
    }

    next()
  } catch (error) {
    console.error('Error in initialize middleware:', error)
    next()
  }
}
